import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Search,
  Plus,
  User,
  LogOut,
  Calendar,
  LayoutDashboard,
  CarFront,
  Building,
  Route,
  Home,
  HelpCircle,
  Shield,
  Bell,
  DollarSign,
  Star,
} from 'lucide-react';
import { useAuth } from '../../../features/auth/contexts/useAuth';
import { Logo } from '../ui/Logo';


export const Navbar: React.FC = () => {
  const { user, isAuthenticated, logout, redirectToLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isNavVisible, setIsNavVisible] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);



  // Handle scroll events to update navbar appearance and visibility
  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down
        setIsNavVisible(false);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up
        setIsNavVisible(true);
      }
      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (userMenuOpen && !target.closest('[data-user-menu]')) {
        setUserMenuOpen(false);
      }
      if (mobileMenuOpen && !target.closest('[data-mobile-menu]')) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen, mobileMenuOpen]);

  const handleSignIn = () => {
    redirectToLogin('', location.pathname);
  };

  const handleQuickCreate = () => {
    if (user?.operator_type === 'transporter') {
      navigate('/trips?create=true');
    }
  };

  const handleSearch = () => {
    if (user?.type === 'traveler') {
      navigate('/trips-discovery');
    } else if (user?.operator_type === 'tour_operator') {
      navigate('/trips');
    } else if (
      user?.type === 'operator' &&
      user?.operator_type === 'transporter'
    ) {
      navigate('/trips');
    }
  };

  const mainNavItems = [
    { label: 'Safaris', path: '/safaris-discovery', icon: Route },
    { label: 'Home', path: '/', icon: Home },
    { label: 'Trips', path: '/transport-trips', icon: CarFront },
  ];

  // Get contextual icon based on user type
  const getContextualIcon = () => {
    if (!isAuthenticated) return null;

    if (user?.operator_type === 'transporter') {
      return Plus;
    } else if (
      user?.type === 'traveler' ||
      user?.operator_type === 'tour_operator'
    ) {
      return Search;
    }
    return null;
  };

  const ContextualIcon = getContextualIcon();

  return (
    <header
      className={`fixed top-3 left-0 right-0 z-50 transition-all duration-500 ease-in-out ${isNavVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Single Island Navbar */}
        <div className="bg-white/90 border border-gray-200 rounded-xl px-6 py-4 shadow-md">
          <div className="flex items-center justify-between">
            
            {/* Left Section - Logo */}
            <div
              className="flex items-center cursor-pointer group"
              onClick={() => navigate('/')}
            >
              <Logo width={74} />
            </div>

            {/* Middle Section - Navigation (Desktop Only) */}
            <div className="hidden lg:flex items-center">
              <div className="flex items-center gap-8">
                {mainNavItems.map(item => {
                  const isActive = location.pathname === item.path;
                  return (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className={
                        `text-sm font-medium transition-all duration-300 ease-in-out ` +
                        (isActive
                          ? 'text-primary-600 font-semibold'
                          : 'text-gray-600 hover:text-gray-900')
                      }
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right Section - Auth/User Menu (Desktop Only) */}
            <div className="hidden lg:flex items-center gap-3">
              {/* Authenticated User Menu */}
              {isAuthenticated ? (
                <div className="relative" data-user-menu>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl transition-all duration-200 text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                    aria-label="User menu"
                  >
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-gray-600" />
                    </div>
                  </button>

                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white text-gray-700 rounded-xl shadow-lg py-2 z-50 border border-gray-200">
                      {/* Grouped profile menu items by category with dividers */}
                      {/* Overview */}
                      <div className="mb-2">
                        <button
                          onClick={() => {
                            navigate('/dashboard');
                            setUserMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-100"
                        >
                          <LayoutDashboard className="w-4 h-4" />
                          <span>Dashboard</span>
                        </button>
                        <button
                          onClick={() => {
                            navigate('/bookings');
                            setUserMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-100"
                        >
                          <Calendar className="w-4 h-4" />
                          <span>Bookings</span>
                        </button>
                        <button
                          onClick={() => {
                            navigate('/trips');
                            setUserMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-100"
                        >
                          <Route className="w-4 h-4" />
                          <span>Trips</span>
                        </button>
                      </div>
                      <div className="border-t border-gray-100 my-1"></div>
                      {/* Business (only for operators) */}
                      {user?.type === 'operator' && (
                        <div className="mb-2">
                          <button
                            onClick={() => {
                              navigate('/company');
                              setUserMenuOpen(false);
                            }}
                            className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-100"
                          >
                            <Building className="w-4 h-4" />
                            <span>Company</span>
                          </button>
                          {user?.operator_type === 'transporter' && (
                            <button
                              onClick={() => {
                                navigate('/fleet');
                                setUserMenuOpen(false);
                              }}
                              className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-100"
                            >
                              <CarFront className="w-4 h-4" />
                              <span>Fleet</span>
                            </button>
                          )}
                          <button
                            onClick={() => {
                              navigate('/verification-required');
                              setUserMenuOpen(false);
                            }}
                            className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-100"
                          >
                            <Shield className="w-4 h-4" />
                            <span>Verification</span>
                          </button>
                          <button
                            onClick={() => {
                              navigate('/payments');
                              setUserMenuOpen(false);
                            }}
                            className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-100"
                          >
                            <DollarSign className="w-4 h-4" />
                            <span>Payments</span>
                          </button>
                          <button
                            onClick={() => {
                              navigate('/reviews');
                              setUserMenuOpen(false);
                            }}
                            className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-100"
                          >
                            <Star className="w-4 h-4" />
                            <span>Reviews</span>
                          </button>
                        </div>
                      )}
                      {user?.type === 'operator' && (
                        <div className="border-t border-gray-100 my-1"></div>
                      )}
                      {/* Account */}
                      <div className="mb-2">
                        <button
                          onClick={() => {
                            navigate('/account');
                            setUserMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-100"
                        >
                          <User className="w-4 h-4" />
                          <span>Account</span>
                        </button>
                        <button
                          onClick={() => {
                            navigate('/notifications');
                            setUserMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-100"
                        >
                          <Bell className="w-4 h-4" />
                          <span>Notifications</span>
                        </button>
                      </div>
                      <div className="border-t border-gray-100 my-1"></div>
                      {/* Support */}
                      <div className="mb-2">
                        <button
                          onClick={() => {
                            navigate('/help');
                            setUserMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-gray-100"
                        >
                          <HelpCircle className="w-4 h-4" />
                          <span>Help Center</span>
                        </button>
                      </div>
                      <div className="border-t border-gray-100 my-1"></div>
                      <button
                        onClick={() => {
                          logout();
                          setUserMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                /* Auth Buttons for Non-Authenticated Users */
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleSignIn}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-primary-600 transition-all duration-300 transform hover:scale-105"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => navigate('/register')}
                    className="px-4 py-2 text-sm font-semibold text-white bg-primary-500 rounded-xl hover:bg-primary-600 transition-all duration-300 shadow-soft hover:shadow-large transform hover:scale-105"
                  >
                    Get Started
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Hamburger Menu - Only on smaller screens */}
            <div className="lg:hidden relative" data-mobile-menu>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-xl transition-all duration-200 text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                aria-label="Mobile menu"
              >
                {/* Custom hamburger icon with 3 lines getting shorter from top to bottom */}
                <div className="flex flex-col gap-1 items-end">
                  <div className="w-6 h-0.5 bg-current"></div>
                  <div className="w-5 h-0.5 bg-current"></div>
                  <div className="w-4 h-0.5 bg-current"></div>
                </div>
              </button>

              {mobileMenuOpen && (
                <div className="fixed left-0 right-0 mx-auto top-20 max-w-sm w-[95vw] max-h-[80vh] overflow-y-auto bg-white text-gray-900 rounded-xl shadow-2xl py-8 px-6 z-[100] flex flex-col gap-6 border border-gray-200">
                  
                  {/* Navigation Links */}
                  <div className="flex flex-col gap-2">
                    {mainNavItems.map(item => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;
                      return (
                        <button
                          key={item.path}
                          onClick={() => {
                            navigate(item.path);
                            setMobileMenuOpen(false);
                          }}
                          className={`w-full text-left px-6 py-4 text-lg font-semibold flex items-center gap-3 rounded-xl transition-colors ${
                            isActive 
                              ? 'bg-primary-50 text-primary-600 border border-primary-100' 
                              : 'hover:bg-gray-50'
                          }`}
                        >
                          <Icon className="w-6 h-6" />
                          <span>{item.label}</span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Divider */}
                  <div className="my-2 border-t border-gray-200"></div>

                  {/* Contextual Action Button */}
                  {ContextualIcon && (
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        if (user?.operator_type === 'transporter') {
                          handleQuickCreate();
                        } else {
                          handleSearch();
                        }
                      }}
                      className="w-full text-left px-6 py-4 text-lg font-semibold flex items-center gap-3 rounded-xl hover:bg-primary-50/80 transition-colors border border-primary-100"
                    >
                      <ContextualIcon className="w-6 h-6" />
                      <span>
                        {user?.operator_type === 'transporter' ? 'Create Trip' : 'Search Trips'}
                      </span>
                    </button>
                  )}

                  {/* Auth Section */}
                  {!isAuthenticated ? (
                    <>
                      <div className="my-2 border-t border-gray-200"></div>
                      <div className="flex flex-col gap-3">
                        <button
                          onClick={() => {
                            setMobileMenuOpen(false);
                            handleSignIn();
                          }}
                          className="w-full text-left px-6 py-4 text-lg font-semibold flex items-center gap-3 rounded-xl hover:bg-primary-50/80 transition-colors border border-primary-100"
                        >
                          <User className="w-6 h-6" />
                          <span>Sign In</span>
                        </button>
                        <button
                          onClick={() => {
                            setMobileMenuOpen(false);
                            navigate('/register');
                          }}
                          className="w-full text-left px-6 py-4 text-lg font-semibold flex items-center gap-3 bg-primary-500 text-white hover:bg-primary-600 transition-colors rounded-xl shadow-soft"
                        >
                          <Plus className="w-6 h-6" />
                          <span>Get Started</span>
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="my-2 border-t border-gray-200"></div>
                      {/* User Menu Items for Mobile */}
                      <div className="flex flex-col gap-2">
                        <button
                          onClick={() => {
                            navigate('/dashboard');
                            setMobileMenuOpen(false);
                          }}
                          className="w-full text-left px-6 py-4 text-lg font-semibold flex items-center gap-3 rounded-xl hover:bg-gray-50 transition-colors"
                        >
                          <LayoutDashboard className="w-6 h-6" />
                          <span>Dashboard</span>
                        </button>
                        <button
                          onClick={() => {
                            navigate('/bookings');
                            setMobileMenuOpen(false);
                          }}
                          className="w-full text-left px-6 py-4 text-lg font-semibold flex items-center gap-3 rounded-xl hover:bg-gray-50 transition-colors"
                        >
                          <Calendar className="w-6 h-6" />
                          <span>Bookings</span>
                        </button>
                        <button
                          onClick={() => {
                            navigate('/trips');
                            setMobileMenuOpen(false);
                          }}
                          className="w-full text-left px-6 py-4 text-lg font-semibold flex items-center gap-3 rounded-xl hover:bg-gray-50 transition-colors"
                        >
                          <Route className="w-6 h-6" />
                          <span>Trips</span>
                        </button>
                        <button
                          onClick={() => {
                            navigate('/account');
                            setMobileMenuOpen(false);
                          }}
                          className="w-full text-left px-6 py-4 text-lg font-semibold flex items-center gap-3 rounded-xl hover:bg-gray-50 transition-colors"
                        >
                          <User className="w-6 h-6" />
                          <span>Account</span>
                        </button>
                        <button
                          onClick={() => {
                            logout();
                            setMobileMenuOpen(false);
                          }}
                          className="w-full text-left px-6 py-4 text-lg font-semibold flex items-center gap-3 rounded-xl hover:bg-red-50 transition-colors text-red-600"
                        >
                          <LogOut className="w-6 h-6" />
                          <span>Sign Out</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};
